import React, { useCallback, useEffect, useRef, useState } from 'react';
import { acknowledgementsApi } from '../../services/api';
import { formatDateTime } from './formatDate';
import { AlertCircle, CheckCircle2, ClipboardCheck, Download, RefreshCw, Search } from 'lucide-react';

const PAGE_SIZE = 50;

function formatDate(value) {
  return formatDateTime(value, 'Not yet');
}

function StatusBadge({ status }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-green-50 text-green-700 border-green-200">
      <CheckCircle2 className="w-3.5 h-3.5" />
      {status}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[...Array(4)].map((_, index) => (
        <td key={index} className="px-4 py-3">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

export default function AcknowledgementManagement() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const fetchId = useRef(0);

  const fetchReport = useCallback(async (silent = false) => {
    const id = ++fetchId.current;
    if (silent) setRefreshing(true);
    else setLoading(true);

    try {
      setError('');
      const data = await acknowledgementsApi.getReport({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search,
        status: 'acknowledged',
        contentType: 'policy',
        groupBy: 'user'
      });
      if (id !== fetchId.current) return;
      if (!Array.isArray(data.rows) || !Number.isFinite(Number(data.total)) || !data.summary) {
        throw new Error('Invalid acknowledgement report from server');
      }
      setRows(data.rows);
      setTotal(Number(data.total));
      setSummary(data.summary);
    } catch (err) {
      if (id === fetchId.current) setError(err.message || 'Failed to load acknowledgement report');
    } finally {
      if (id === fetchId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [page, search]);

  useEffect(() => {
    fetchReport(false);
    return () => { fetchId.current += 1; };
  }, [fetchReport]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  function updateSearch(value) {
    setSearch(value);
    setPage(0);
  }

  async function exportReport() {
    try {
      setExporting(true);
      await acknowledgementsApi.exportReport({ search, status: 'acknowledged', contentType: 'policy', groupBy: 'user' });
    } catch (err) {
      alert('Failed to export report: ' + err.message);
    } finally {
      setExporting(false);
    }
  }

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load acknowledgements</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button onClick={() => fetchReport(false)} className="px-6 py-3 bg-[#00AAFF] text-white font-medium rounded-xl hover:bg-[#0088cc] transition-colors">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-[#00AAFF]" />
            Acknowledgements
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Track whether users have completed HR Hub policy acknowledgements.
          </p>
        </div>
      </div>

      <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-start">
        <div className="relative w-full lg:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(event) => updateSearch(event.target.value)}
            placeholder="Search users..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#00AAFF] focus:ring-2 focus:ring-[#00AAFF]/20"
          />
        </div>

        <button
          onClick={() => fetchReport(true)}
          disabled={refreshing}
          className="inline-flex w-full lg:w-auto items-center justify-center gap-1.5 whitespace-nowrap text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>

        <button
          onClick={exportReport}
          disabled={exporting}
          className="inline-flex w-full lg:w-auto items-center justify-center gap-1.5 whitespace-nowrap text-sm px-3 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0088cc] transition-colors disabled:opacity-50"
        >
          <Download className="w-3.5 h-3.5" />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Acknowledged users</p>
          <p className="mt-1 text-2xl font-bold text-green-600">{loading ? 'Loading…' : refreshing ? 'Refreshing…' : summary?.acknowledged ?? '—'}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Acknowledged At</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">HR Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(8)].map((_, index) => <SkeletonRow key={index} />)
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <ClipboardCheck className="w-10 h-10 text-gray-300" />
                      <p className="font-medium text-gray-500">No acknowledgement records found</p>
                      <p className="text-sm">No users match the current filters.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.user_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate max-w-[220px]">{row.user_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[220px]">{row.user_email || `User ID ${row.user_id}`}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(row.acknowledged_at)}</td>
                    <td className="px-4 py-3">
                      {row.notification_status ? (
                        <div className="text-xs text-gray-500">
                          <p className="capitalize font-medium text-gray-700">{row.notification_status}</p>
                          <p>{formatDate(row.notification_sent_at)}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not triggered</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-500">
              Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(value => value - 1)}
                disabled={page === 0}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">{page + 1} / {totalPages}</span>
              <button
                onClick={() => setPage(value => value + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
